import { ref, getDownloadURL } from "firebase/storage";

import { storage } from "./firebase";

const URL_CACHE_KEY = "acc_clothes_url_cache_v2";
const DATA_URL_CACHE_KEY = "acc_clothes_dataurl_cache_v1";
const URL_TTL_MS = 100 * 60 * 1000;
const URL_REFRESH_GRACE_MS = 60 * 1000;

interface CachedUrlEntry {
  url: string;
  expiresAt: number;
}

const inMemoryUrlCache = new Map<string, CachedUrlEntry>();
const inflightUrlLookups = new Map<string, Promise<string>>();
const inMemoryDataUrlCache = new Map<string, string>();
const inflightDataUrlLookups = new Map<string, Promise<string>>();

let urlCacheHydrated = false;
let dataUrlCacheHydrated = false;
let urlPersistTimer: number | undefined;
let dataUrlPersistTimer: number | undefined;

function hydrateUrlCache(): void {
  if (urlCacheHydrated) return;
  urlCacheHydrated = true;
  try {
    const raw = localStorage.getItem(URL_CACHE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return;
    const now = Date.now();
    Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
      if (
        value &&
        typeof value === "object" &&
        "url" in value &&
        "expiresAt" in value
      ) {
        const entry = value as { url: unknown; expiresAt: unknown };
        if (
          typeof entry.url === "string" &&
          typeof entry.expiresAt === "number" &&
          entry.expiresAt > now
        ) {
          inMemoryUrlCache.set(key, { url: entry.url, expiresAt: entry.expiresAt });
        }
      }
    });
  } catch {
    // Best effort.
  }
}

function scheduleUrlPersist(): void {
  if (urlPersistTimer !== undefined) return;
  urlPersistTimer = window.setTimeout(() => {
    urlPersistTimer = undefined;
    try {
      const obj: Record<string, CachedUrlEntry> = {};
      const now = Date.now();
      inMemoryUrlCache.forEach((entry, key) => {
        if (entry.expiresAt > now) obj[key] = entry;
      });
      localStorage.setItem(URL_CACHE_KEY, JSON.stringify(obj));
    } catch {
      // Quota / private mode.
    }
  }, 1000);
}

function hydrateDataUrlCache(): void {
  if (dataUrlCacheHydrated) return;
  dataUrlCacheHydrated = true;
  try {
    const raw = sessionStorage.getItem(DATA_URL_CACHE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return;
    Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
      if (typeof value === "string") inMemoryDataUrlCache.set(key, value);
    });
  } catch {
    // Best effort.
  }
}

function scheduleDataUrlPersist(): void {
  if (dataUrlPersistTimer !== undefined) return;
  dataUrlPersistTimer = window.setTimeout(() => {
    dataUrlPersistTimer = undefined;
    try {
      const obj: Record<string, string> = {};
      inMemoryDataUrlCache.forEach((value, key) => {
        obj[key] = value;
      });
      sessionStorage.setItem(DATA_URL_CACHE_KEY, JSON.stringify(obj));
    } catch {
      // Quota — best effort. Data URLs can be large; we tolerate eviction.
    }
  }, 1500);
}

export async function resolveStoragePathToUrl(path: string): Promise<string> {
  hydrateUrlCache();

  const cached = inMemoryUrlCache.get(path);
  if (cached && cached.expiresAt > Date.now() + URL_REFRESH_GRACE_MS) {
    return cached.url;
  }

  const inflight = inflightUrlLookups.get(path);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      const url = await getDownloadURL(ref(storage, path));
      inMemoryUrlCache.set(path, {
        url,
        expiresAt: Date.now() + URL_TTL_MS,
      });
      scheduleUrlPersist();
      return url;
    } finally {
      inflightUrlLookups.delete(path);
    }
  })();

  inflightUrlLookups.set(path, promise);
  return promise;
}

export async function urlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download image (${response.status}).`);
  }
  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read image data."));
    reader.readAsDataURL(blob);
  });
}

export async function pathToDataUrl(path: string): Promise<string> {
  hydrateDataUrlCache();

  const cached = inMemoryDataUrlCache.get(path);
  if (cached) return cached;

  const inflight = inflightDataUrlLookups.get(path);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      const url = await resolveStoragePathToUrl(path);
      const dataUrl = await urlToDataUrl(url);
      inMemoryDataUrlCache.set(path, dataUrl);
      scheduleDataUrlPersist();
      return dataUrl;
    } finally {
      inflightDataUrlLookups.delete(path);
    }
  })();

  inflightDataUrlLookups.set(path, promise);
  return promise;
}
